import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CloudOff,
  Loader2,
  LogIn,
  RefreshCw,
  UserPlus,
  Users,
} from "lucide-react";
import ProfileConnectionRow from "../components/profile/ProfileConnectionRow";
import { selectAppUser } from "../redux/appAuthSlice";
import { fetchDashboardProfile } from "../redux/dashboardSlice";
import {
  clearConnections,
  fetchUserFollowList,
  toggleFollow,
} from "../redux/profileSlice";
import {
  normalizeConnectionListType,
  PROFILE_CONNECTION_TYPES,
} from "../utils/profileHelpers";
import "../profile.css";

const ProfileConnections = () => {
  const { listType: listTypeParam } = useParams();
  const dispatch = useDispatch();
  const authUser = useSelector(selectAppUser);
  const userId = authUser?._id;
  const { profile } = useSelector((s) => s.dashboard);
  const {
    connections,
    connectionsStatus,
    connectionsError,
    connectionsType,
  } = useSelector((s) => s.profile);

  const listType = normalizeConnectionListType(listTypeParam);
  const meta = PROFILE_CONNECTION_TYPES[listType];
  const [refreshing, setRefreshing] = useState(false);
  const [busyIds, setBusyIds] = useState({});

  const myFollowingIds = profile?.following || authUser?.following || [];

  const loadList = useCallback(
    async (isRefresh = false) => {
      if (!userId) return;
      if (isRefresh) setRefreshing(true);
      try {
        await dispatch(fetchUserFollowList({ userId, type: listType })).unwrap();
      } finally {
        if (isRefresh) setRefreshing(false);
      }
    },
    [dispatch, userId, listType]
  );

  useEffect(() => {
    if (!userId) return undefined;
    loadList();
    return () => dispatch(clearConnections());
  }, [dispatch, loadList, userId]);

  const followingSet = useMemo(
    () => new Set((myFollowingIds || []).map((id) => String(id))),
    [myFollowingIds]
  );

  const handleToggle = async (targetUser) => {
    if (!userId || !targetUser?._id) return;
    if (String(userId) === String(targetUser._id)) return;

    const targetId = targetUser._id;
    setBusyIds((prev) => ({ ...prev, [targetId]: true }));

    try {
      await dispatch(
        toggleFollow({ currentUserId: userId, targetId })
      ).unwrap();
      await dispatch(fetchDashboardProfile({ userId, force: true }));
    } catch (error) {
      window.alert(
        typeof error === "string" ? error : "Could not update follow status."
      );
    } finally {
      setBusyIds((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    }
  };

  const handleRefresh = async () => {
    await Promise.all([
      loadList(true),
      userId ? dispatch(fetchDashboardProfile({ userId, force: true })) : Promise.resolve(),
    ]);
  };

  const loading = connectionsStatus === "loading" && !refreshing && !connections.length;
  const failed = connectionsStatus === "failed" && !connections.length;
  const rows =
    connectionsType === listType ? connections : connectionsStatus === "loading" ? [] : connections;

  if (!userId) {
    return (
      <div className="prof-page">
        <div className="prof-state">
          <LogIn size={32} />
          <h1>Sign in to view your network</h1>
          <Link to="/app/login" className="prof-primary-btn">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="prof-page prof-conn-page">
      <div className="prof-conn-shell">
        <header className="prof-conn-head">
          <div className="prof-conn-head-accent" aria-hidden />
          <div className="prof-conn-head-row">
            <div className="prof-conn-head-copy">
              <p className="prof-kicker">Your network</p>
              <h1>{meta.title}</h1>
            </div>

            <div className="prof-conn-count">
              <Users size={10} aria-hidden />
              <span className="tabular-nums">{rows.length}</span>
            </div>

            <button
              type="button"
              className="prof-conn-refresh"
              onClick={handleRefresh}
              disabled={refreshing || connectionsStatus === "loading"}
              aria-label="Refresh list"
            >
              <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>

          <div className="prof-conn-tabs" role="tablist" aria-label="Connection lists">
            {Object.values(PROFILE_CONNECTION_TYPES).map((tab) => {
              const active = tab.id === listType;
              return (
                <Link
                  key={tab.id}
                  to={`/app/profile/connections/${tab.id}`}
                  role="tab"
                  aria-selected={active}
                  className={`prof-conn-tab${active ? " prof-conn-tab-active" : ""}`}
                >
                  {tab.title}
                </Link>
              );
            })}
          </div>
        </header>

        <div className="prof-conn-body">
          {loading ? (
            <div className="prof-state">
              <Loader2 size={18} className="animate-spin" />
              <p>Loading {meta.title.toLowerCase()}…</p>
            </div>
          ) : failed ? (
            <div className="prof-state">
              <CloudOff size={22} />
              <p>{String(connectionsError || "Could not load this list.")}</p>
              <button type="button" className="prof-primary-btn" onClick={handleRefresh}>
                <RefreshCw size={13} />
                Try again
              </button>
            </div>
          ) : rows.length ? (
            <div className="prof-conn-list">
              {rows.map((user) => (
                <ProfileConnectionRow
                  key={user._id}
                  user={user}
                  listType={listType}
                  isFollowing={followingSet.has(String(user._id))}
                  onToggle={handleToggle}
                  busy={Boolean(busyIds[user._id])}
                />
              ))}
            </div>
          ) : (
            <div className="prof-state prof-conn-empty">
              {listType === "followers" ? <Users size={24} /> : <UserPlus size={24} />}
              <h2>{meta.emptyTitle}</h2>
              <p>{meta.emptyText}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileConnections;
